import React, { useEffect, useState, useCallback } from "react";
import {
  Spin, Alert, Table, Tag, Input, Select, Button, Drawer, Form,
  DatePicker, Segmented, message, Badge, Tooltip,
} from "antd";
import {
  SearchOutlined, PlusOutlined, AppstoreOutlined, UnorderedListOutlined,
  PhoneOutlined, MailOutlined, CalendarOutlined, EnvironmentOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import customClientAxios from "../../customAxios";
import { AdminLeads, AdminLeadStats } from "../../utils/ApiEndpoints";
import { PageContainer, FilterRow } from "../paUtils";
import LeadDetailDrawer from "./LeadDetailDrawer";
import styled from "styled-components";

// ── Styled ────────────────────────────────────────────────────────────────

const StatsBar = styled.div`
  display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap;
`;
const StatPill = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 8px 16px; border-radius: 10px;
  background: white; border: 1px solid #e2e8f0;
  font-size: 13px; color: #374151;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
`;
const StatNum = styled.span`font-weight: 700; color: #1e293b; font-size: 16px;`;
const StatLabel = styled.span`color: #64748b; font-size: 12px;`;

const TableCard = styled.div`
  background: white; border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  border: 1px solid #e2e8f0; overflow: hidden;
`;
const TableHeader = styled.div`
  padding: 16px 24px; display: flex; align-items: center;
  justify-content: space-between; gap: 12px; flex-wrap: wrap;
  border-bottom: 1px solid #f1f5f9;
`;
const TableTitle = styled.div`font-size: 15px; font-weight: 600; color: #1e293b;`;

// Kanban
const KanbanContainer = styled.div`
  display: flex; gap: 12px; overflow-x: auto;
  padding-bottom: 12px; min-height: 400px;
`;
const KanbanColumn = styled.div`
  min-width: 240px; width: 240px; flex-shrink: 0;
  background: #f8fafc; border-radius: 12px;
  border: 1px solid #e2e8f0; display: flex;
  flex-direction: column; max-height: 70vh;
`;
const KanbanColumnHeader = styled.div`
  padding: 12px 14px; font-size: 13px; font-weight: 600;
  color: #1e293b; display: flex; align-items: center;
  justify-content: space-between; border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
`;
const KanbanCards = styled.div`
  flex: 1; overflow-y: auto; padding: 8px;
  display: flex; flex-direction: column; gap: 8px;
  scrollbar-width: thin;
`;
const KanbanCard = styled.div`
  background: white; border-radius: 10px; padding: 12px;
  border: 1px solid #e2e8f0; cursor: pointer;
  transition: all 0.15s ease; box-shadow: 0 1px 2px rgba(0,0,0,0.04);
  &:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.08); transform: translateY(-1px); }
`;
const CardName = styled.div`font-size: 13px; font-weight: 600; color: #1e293b; margin-bottom: 4px;`;
const CardMeta = styled.div`font-size: 11px; color: #94a3b8; display: flex; align-items: center; gap: 4px; margin-top: 2px;`;

// ── Constants ──────────────────────────────────────────────────────────────

const STAGE_COLORS = {
  new: "blue",
  contacted: "cyan",
  qualified: "purple",
  negotiating: "orange",
  won: "green",
  activated: "lime",
  purchased: "gold",
  lost: "red",
};
const PRIORITY_COLORS = { hot: "red", warm: "orange", cold: "blue" };
const STAGES = [
  "new",
  "contacted",
  "qualified",
  "negotiating",
  "won",
  "activated",
  "purchased",
  "lost",
];

// Funnel mapping. Mirrors backend services/hubspot/mappers.js and the
// FUNNEL_STAGE map in LeadDetailDrawer.js.
const FUNNEL_LABEL = {
  new: "Prospect",
  contacted: "Prospect",
  qualified: "Lead",
  negotiating: "Lead",
  won: "Onboarded",
  activated: "Activated",
  purchased: "Purchased",
  lost: "Lost",
};
const PRIORITIES = ["hot", "warm", "cold"];
const SOURCES = ["telegram", "manual", "referral", "instagram", "event", "website", "other"];
const SOCIAL_PLATFORMS = ["instagram", "youtube", "linkedin", "twitter", "facebook", "website", "other"];

// ── Component ──────────────────────────────────────────────────────────────

function LeadsTab() {
  const [view, setView] = useState("table");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  // "" = all, "true" = only with phone, "false" = only without phone.
  // String values are what the backend expects (Express query strings).
  const [phoneFilter, setPhoneFilter] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm] = Form.useForm();
  const [addLoading, setAddLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: 50, sortBy: "updatedAt", sortOrder: "desc" };
      if (search.trim()) params.search = search.trim();
      if (stageFilter) params.stage = stageFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (phoneFilter) params.hasPhone = phoneFilter;
      const res = await customClientAxios().get(AdminLeads, { params });
      setLeads(res.data.data?.leads || []);
      setTotal(res.data.data?.pagination?.total || 0);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, [page, search, stageFilter, priorityFilter, sourceFilter, phoneFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await customClientAxios().get(AdminLeadStats);
      setStats(res.data.data);
    } catch {}
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { setPage(1); }, [search, stageFilter, priorityFilter, sourceFilter, phoneFilter]);

  const handleRefresh = () => { fetchLeads(); fetchStats(); };

  const openLead = (leadId) => { setSelectedLeadId(leadId); setDrawerOpen(true); };

  const handleAddLead = async (values) => {
    setAddLoading(true);
    try {
      const socialLinks = (values.socialLinks || []).filter((s) => s?.url?.trim());
      await customClientAxios().post(AdminLeads, {
        ...values,
        socialLinks,
        tags: values.tags ? values.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      });
      message.success("Contact created");
      setAddModalOpen(false);
      addForm.resetFields();
      handleRefresh();
    } catch (e) {
      message.error(e?.response?.data?.error || "Failed to create contact");
    } finally {
      setAddLoading(false);
    }
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const baseParams = { limit: 200, sortBy: "updatedAt", sortOrder: "desc" };
      if (search.trim()) baseParams.search = search.trim();
      if (stageFilter) baseParams.stage = stageFilter;
      if (priorityFilter) baseParams.priority = priorityFilter;
      if (sourceFilter) baseParams.source = sourceFilter;
      if (phoneFilter) baseParams.hasPhone = phoneFilter;

      const all = [];
      let pageNum = 1;
      let totalPages = 1;
      do {
        const res = await customClientAxios().get(AdminLeads, {
          params: { ...baseParams, page: pageNum },
        });
        const batch = res.data.data?.leads || [];
        all.push(...batch);
        totalPages = res.data.data?.pagination?.totalPages || 1;
        pageNum += 1;
      } while (pageNum <= totalPages);

      if (!all.length) {
        message.info("No contacts to export");
        return;
      }

      const csvEscape = (val) => {
        if (val === null || val === undefined) return "";
        const s = String(val).replace(/"/g, '""');
        return /[",\n\r]/.test(s) ? `"${s}"` : s;
      };
      const fmtDate = (v) => (v ? new Date(v).toISOString() : "");
      const fmtSocial = (links) =>
        (links || []).map((l) => `${l.platform || ""}:${l.url || ""}`).join(" | ");

      const headers = [
        "leadId", "name", "email", "phone", "category", "city",
        "stage", "priority", "source", "sourceUrl",
        "tags", "socialLinks", "followUpDate", "createdAt", "updatedAt",
      ];
      const rows = all.map((l) => [
        l.leadId, l.name, l.email, l.phone, l.category, l.city,
        l.stage, l.priority, l.source, l.sourceUrl,
        (l.tags || []).join(", "), fmtSocial(l.socialLinks),
        fmtDate(l.followUpDate), fmtDate(l.createdAt), fmtDate(l.updatedAt),
      ]);
      const csv = [headers, ...rows]
        .map((row) => row.map(csvEscape).join(","))
        .join("\n");

      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `leads-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      message.success(`Exported ${all.length} contacts`);
    } catch (e) {
      message.error(e?.response?.data?.error || "Failed to export contacts");
    } finally {
      setExportLoading(false);
    }
  };

  const handleKanbanStageChange = async (leadId, newStage) => {
    try {
      await customClientAxios().patch(`${AdminLeads}/${leadId}`, { stage: newStage });
      handleRefresh();
    } catch (e) {
      message.error("Failed to update stage");
    }
  };

  // ── Table columns ─────────────────────────────────────────────────────────

  const columns = [
    {
      title: "Name",
      key: "name",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>{r.name || "—"}</div>
          {r.category && <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.category}</div>}
        </div>
      ),
    },
    {
      title: "Contact",
      key: "contact",
      render: (_, r) => (
        <div>
          {r.phone && <div style={{ fontSize: 12 }}>{r.phone}</div>}
          {r.email && <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.email}</div>}
        </div>
      ),
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      width: 100,
      render: (v) => v || "—",
    },
    {
      title: "Funnel / Stage",
      dataIndex: "stage",
      key: "stage",
      width: 150,
      render: (v) => (
        <div>
          <Tag color={STAGE_COLORS[v]} style={{ marginBottom: 2 }}>{v}</Tag>
          <div style={{ fontSize: 10, color: "#64748b" }}>{FUNNEL_LABEL[v] || "—"}</div>
        </div>
      ),
    },
    {
      title: "HubSpot",
      key: "hubspot",
      width: 90,
      render: (_, r) => {
        if (r.hubspotContactId) {
          return (
            <Tooltip title={`Contact ID: ${r.hubspotContactId}${r.hubspotSyncedAt ? `\nSynced: ${new Date(r.hubspotSyncedAt).toLocaleString()}` : ""}`}>
              <Tag color="green" style={{ fontSize: 10 }}>synced</Tag>
            </Tooltip>
          );
        }
        if (r.hubspotSyncError) {
          return (
            <Tooltip title={r.hubspotSyncError}>
              <Tag color="red" style={{ fontSize: 10 }}>error</Tag>
            </Tooltip>
          );
        }
        return <Tag style={{ fontSize: 10 }}>pending</Tag>;
      },
    },
    {
      title: "Nurture",
      key: "nurture",
      width: 100,
      render: (_, r) => {
        if (r.optedOutOfNurture) return <Tag color="default" style={{ fontSize: 10 }}>opted out</Tag>;
        const status = r.nurtureStatus || "not_started";
        const colorMap = {
          active: "blue",
          paused: "orange",
          completed: "green",
          opted_out: "default",
          not_started: "default",
        };
        return <Tag color={colorMap[status]} style={{ fontSize: 10 }}>{status.replace("_", " ")}</Tag>;
      },
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 80,
      render: (v) => <Tag color={PRIORITY_COLORS[v]}>{v}</Tag>,
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      width: 90,
      render: (v) => <Tag style={{ fontSize: 11 }}>{v}</Tag>,
    },
    {
      title: "Follow-up",
      dataIndex: "followUpDate",
      key: "followUpDate",
      width: 100,
      render: (v) => {
        if (!v) return "—";
        const d = new Date(v);
        const isOverdue = d < new Date(new Date().setHours(0, 0, 0, 0));
        const isToday = d.toDateString() === new Date().toDateString();
        return (
          <span style={{ fontSize: 12, color: isOverdue ? "#ef4444" : isToday ? "#f59e0b" : "#64748b", fontWeight: isOverdue || isToday ? 600 : 400 }}>
            {d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
          </span>
        );
      },
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      render: (v) => (v || []).slice(0, 2).map((t) => <Tag key={t} style={{ fontSize: 10 }}>{t}</Tag>),
    },
    {
      title: "Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 90,
      render: (v) => v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—",
    },
  ];

  // ── Kanban data ───────────────────────────────────────────────────────────

  const kanbanData = STAGES.reduce((acc, stage) => {
    acc[stage] = leads.filter((l) => l.stage === stage);
    return acc;
  }, {});

  return (
    <PageContainer>
      {/* Stats Bar */}
      {stats && (
        <StatsBar>
          <StatPill><StatNum>{stats.total}</StatNum><StatLabel>Total</StatLabel></StatPill>
          <StatPill><StatNum>{stats.thisWeek}</StatNum><StatLabel>This Week</StatLabel></StatPill>
          <StatPill><StatNum>{stats.followUpsToday}</StatNum><StatLabel>Follow-ups Today</StatLabel></StatPill>
          <StatPill><StatNum>{stats.conversionRate}%</StatNum><StatLabel>Conversion</StatLabel></StatPill>
          {/* Funnel-stage aggregates */}
          {stats.funnel && (
            <>
              <StatPill style={{ background: "rgba(99, 102, 241, 0.08)" }}>
                <StatNum style={{ color: "#6366f1" }}>{stats.funnel.prospect || 0}</StatNum>
                <StatLabel>Prospect</StatLabel>
              </StatPill>
              <StatPill style={{ background: "rgba(139, 92, 246, 0.08)" }}>
                <StatNum style={{ color: "#8b5cf6" }}>{stats.funnel.lead || 0}</StatNum>
                <StatLabel>Lead</StatLabel>
              </StatPill>
              <StatPill style={{ background: "rgba(16, 185, 129, 0.08)" }}>
                <StatNum style={{ color: "#10b981" }}>{stats.funnel.onboarded || 0}</StatNum>
                <StatLabel>Onboarded</StatLabel>
              </StatPill>
              <StatPill style={{ background: "rgba(132, 204, 22, 0.08)" }}>
                <StatNum style={{ color: "#65a30d" }}>{stats.funnel.activated || 0}</StatNum>
                <StatLabel>Activated</StatLabel>
              </StatPill>
              <StatPill style={{ background: "rgba(245, 158, 11, 0.10)" }}>
                <StatNum style={{ color: "#d97706" }}>{stats.funnel.purchased || 0}</StatNum>
                <StatLabel>Purchased</StatLabel>
              </StatPill>
            </>
          )}
          {/* HubSpot sync health */}
          {stats.hubspot && (
            <Tooltip title={`Synced: ${stats.hubspot.synced} / Errored: ${stats.hubspot.errored} / Unsynced: ${stats.hubspot.unsynced}`}>
              <StatPill style={{ background: "rgba(244, 132, 90, 0.08)", cursor: "help" }}>
                <StatNum style={{ color: "#f4845a" }}>{stats.hubspot.synced || 0}</StatNum>
                <StatLabel>HubSpot synced</StatLabel>
              </StatPill>
            </Tooltip>
          )}
          {/* Nurture active count */}
          {stats.nurture && (
            <StatPill style={{ background: "rgba(14, 165, 233, 0.08)" }}>
              <StatNum style={{ color: "#0ea5e9" }}>{stats.nurture.active || 0}</StatNum>
              <StatLabel>Nurture active</StatLabel>
            </StatPill>
          )}
          {/* Data quality: missing-phone count. Click to filter the table. */}
          {stats.dataQuality && stats.dataQuality.withoutPhone > 0 && (
            <Tooltip title="Click to filter the table to contacts missing a phone (cannot run WA nurture or Meta audience matching).">
              <StatPill
                onClick={() => setPhoneFilter(phoneFilter === "false" ? "" : "false")}
                style={{
                  background: phoneFilter === "false" ? "rgba(239, 68, 68, 0.16)" : "rgba(239, 68, 68, 0.08)",
                  cursor: "pointer",
                  border: phoneFilter === "false" ? "1px solid #ef4444" : undefined,
                }}
              >
                <StatNum style={{ color: "#ef4444" }}>{stats.dataQuality.withoutPhone}</StatNum>
                <StatLabel>Missing phone</StatLabel>
              </StatPill>
            </Tooltip>
          )}
        </StatsBar>
      )}

      {/* Filters */}
      <FilterRow>
        <Input
          placeholder="Search contacts..."
          prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 220 }}
          allowClear
        />
        <Select
          value={stageFilter}
          onChange={setStageFilter}
          style={{ width: 140 }}
          placeholder="Stage"
          options={[{ value: "", label: "All Stages" }, ...STAGES.map((s) => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))]}
        />
        <Select
          value={priorityFilter}
          onChange={setPriorityFilter}
          style={{ width: 120 }}
          placeholder="Priority"
          options={[{ value: "", label: "All" }, ...PRIORITIES.map((p) => ({ value: p, label: p.charAt(0).toUpperCase() + p.slice(1) }))]}
        />
        <Select
          value={sourceFilter}
          onChange={setSourceFilter}
          style={{ width: 130 }}
          placeholder="Source"
          options={[{ value: "", label: "All Sources" }, ...SOURCES.map((s) => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))]}
        />
        <Select
          value={phoneFilter}
          onChange={setPhoneFilter}
          style={{ width: 160 }}
          placeholder="Phone"
          options={[
            { value: "", label: "All (any phone)" },
            { value: "true", label: "Has phone" },
            { value: "false", label: "Missing phone" },
          ]}
        />
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <Segmented
            options={[
              { value: "table", icon: <UnorderedListOutlined /> },
              { value: "kanban", icon: <AppstoreOutlined /> },
            ]}
            value={view}
            onChange={setView}
          />
          <Button
            icon={<DownloadOutlined />}
            loading={exportLoading}
            onClick={handleExport}
          >
            Export CSV
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>
            Add Contact
          </Button>
        </div>
      </FilterRow>

      {error && <Alert type="error" message={error} style={{ marginBottom: 20 }} />}

      {/* Table View */}
      {view === "table" && (
        <TableCard>
          <Spin spinning={loading}>
            <Table
              dataSource={leads}
              columns={columns}
              rowKey="leadId"
              size="middle"
              pagination={{
                current: page,
                pageSize: 50,
                total,
                onChange: setPage,
                showSizeChanger: false,
                showTotal: (t) => `${t} leads`,
              }}
              onRow={(record) => ({
                onClick: () => openLead(record.leadId),
                style: { cursor: "pointer" },
              })}
              scroll={{ x: 900 }}
            />
          </Spin>
        </TableCard>
      )}

      {/* Kanban View */}
      {view === "kanban" && (
        <Spin spinning={loading}>
          <KanbanContainer>
            {STAGES.map((stage) => (
              <KanbanColumn key={stage}>
                <KanbanColumnHeader>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Tag color={STAGE_COLORS[stage]} style={{ margin: 0, fontSize: 11 }}>
                      {stage.charAt(0).toUpperCase() + stage.slice(1)}
                    </Tag>
                    <Badge count={kanbanData[stage]?.length || 0} style={{ backgroundColor: "#94a3b8" }} size="small" />
                  </div>
                </KanbanColumnHeader>
                <KanbanCards>
                  {(kanbanData[stage] || []).map((lead) => (
                    <KanbanCard key={lead.leadId} onClick={() => openLead(lead.leadId)}>
                      <CardName>{lead.name || "Unnamed"}</CardName>
                      {lead.category && <CardMeta>{lead.category}</CardMeta>}
                      {lead.city && <CardMeta><EnvironmentOutlined style={{ fontSize: 10 }} />{lead.city}</CardMeta>}
                      {lead.phone && <CardMeta><PhoneOutlined style={{ fontSize: 10 }} />{lead.phone}</CardMeta>}
                      <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
                        <Tag color={PRIORITY_COLORS[lead.priority]} style={{ fontSize: 10, margin: 0 }}>{lead.priority}</Tag>
                        <Tag style={{ fontSize: 10, margin: 0 }}>{lead.source}</Tag>
                        {lead.followUpDate && (
                          <Tooltip title={`Follow-up: ${new Date(lead.followUpDate).toLocaleDateString("en-IN")}`}>
                            <Tag icon={<CalendarOutlined />} style={{ fontSize: 10, margin: 0 }}
                              color={new Date(lead.followUpDate) < new Date() ? "red" : "default"}>
                              {new Date(lead.followUpDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                            </Tag>
                          </Tooltip>
                        )}
                      </div>
                      {/* Stage quick-change buttons */}
                      <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
                        {STAGES.filter((s) => s !== stage).slice(0, 3).map((s) => (
                          <Button
                            key={s}
                            size="small"
                            type="text"
                            style={{ fontSize: 10, padding: "0 6px", height: 22, color: "#64748b" }}
                            onClick={(e) => { e.stopPropagation(); handleKanbanStageChange(lead.leadId, s); }}
                          >
                            → {s}
                          </Button>
                        ))}
                      </div>
                    </KanbanCard>
                  ))}
                  {(kanbanData[stage] || []).length === 0 && (
                    <div style={{ padding: 16, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
                      No leads
                    </div>
                  )}
                </KanbanCards>
              </KanbanColumn>
            ))}
          </KanbanContainer>
        </Spin>
      )}

      {/* Lead Detail Drawer */}
      <LeadDetailDrawer
        leadId={selectedLeadId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onUpdate={handleRefresh}
      />

      {/* Add Lead Drawer */}
      <Drawer
        title="Add New Contact"
        placement="right"
        width={480}
        open={addModalOpen}
        onClose={() => { setAddModalOpen(false); addForm.resetFields(); }}
        destroyOnClose
        footer={
          <div style={{ padding: "12px 0" }}>
            <Button type="primary" loading={addLoading} block onClick={() => addForm.submit()}>
              Create Lead
            </Button>
          </div>
        }
      >
        <Form form={addForm} layout="vertical" onFinish={handleAddLead}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: "Name is required" }]}>
            <Input placeholder="Person or business name" />
          </Form.Item>
          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item name="email" label="Email" style={{ flex: 1 }}>
              <Input placeholder="Email" />
            </Form.Item>
            <Form.Item name="phone" label="Phone" style={{ flex: 1 }}>
              <Input placeholder="Phone" />
            </Form.Item>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item name="category" label="Category" style={{ flex: 1 }}>
              <Input placeholder="e.g. events, coaching, fitness" />
            </Form.Item>
            <Form.Item name="city" label="City" style={{ flex: 1 }}>
              <Input placeholder="City" />
            </Form.Item>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item name="priority" label="Priority" style={{ flex: 1 }} initialValue="warm">
              <Select options={PRIORITIES.map((p) => ({ value: p, label: p.charAt(0).toUpperCase() + p.slice(1) }))} />
            </Form.Item>
            <Form.Item name="source" label="Source" style={{ flex: 1 }} initialValue="manual">
              <Select options={SOURCES.map((s) => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))} />
            </Form.Item>
          </div>
          <Form.Item name="sourceUrl" label="Source URL">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.List name="socialLinks">
            {(fields, { add, remove }) => (
              <>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Social Links</div>
                {fields.map(({ key, name }) => (
                  <div key={key} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <Form.Item name={[name, "platform"]} noStyle initialValue="instagram">
                      <Select style={{ width: 120 }} options={SOCIAL_PLATFORMS.map((p) => ({ value: p, label: p }))} />
                    </Form.Item>
                    <Form.Item name={[name, "url"]} noStyle>
                      <Input placeholder="URL" style={{ flex: 1 }} />
                    </Form.Item>
                    <Button danger size="small" onClick={() => remove(name)}>✕</Button>
                  </div>
                ))}
                <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} size="small" style={{ marginBottom: 12 }}>
                  Add Social Link
                </Button>
              </>
            )}
          </Form.List>
          <Form.Item name="tags" label="Tags (comma separated)">
            <Input placeholder="e.g. high-value, dance, hyderabad" />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} placeholder="Initial notes about this contact..." />
          </Form.Item>
        </Form>
      </Drawer>
    </PageContainer>
  );
}

export default LeadsTab;
